import React from 'react';
import { BaseModal } from '../BaseModal';
import type { Product } from '../../pages/CatalogPage';
import type { Country } from '../../types';

type ProductDetailModalProps = {
  product: Product;
  onClose: () => void;
  visibleCountries: Country[];
};

const deviceImageRules: { matches: (s: string) => boolean; image: string | null }[] = [
  { matches: s => s.includes('pos terminal'), image: '/skytab-pos-terminal.png' },
  { matches: s => s.includes('kds'), image: '/skytab-kds.png' },
  { matches: s => s.includes('glass'), image: '/skytab-glass.png' },
  { matches: s => s.includes('air'), image: '/skytab-air.png' },
  { matches: s => s.includes('mobile ordering') || s.includes('mobile wallet'), image: null },
  { matches: s => s.includes('mobile'), image: '/skytab-mobile.png' },
  { matches: s => s.includes('kiosk'), image: '/skytab-kiosk.png' },
  { matches: s => s.includes('cfd') || s.includes('customer facing'), image: '/skytab-cfd.png' },
];

const getDeviceImage = (solution: string): string | null => {
  const s = solution.toLowerCase();
  const rule = deviceImageRules.find(r => r.matches(s));
  return rule ? rule.image : null;
};

type CountryAvailabilityProps = { product: Product; visibleCountries: Country[] };

function CountryAvailability({ product, visibleCountries }: CountryAvailabilityProps) {
  return (
    <div>
      <h3 className="text-sm font-medium text-foreground mb-2">Country Availability</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {visibleCountries.map(country => {
          const availability = product.countries[country.code];
          return (
            <div key={country.code} className="flex items-center gap-2 text-sm">
              <span className="text-base">{country.flag}</span>
              <span className="text-muted-foreground">{country.code}</span>
              <span>
                {availability === true ? (
                  <span className="text-green-600 font-semibold">✓</span>
                ) : availability === 'Coming Soon' ? (
                  <span className="text-yellow-600 text-xs italic">Soon</span>
                ) : (
                  <span className="text-red-500 font-semibold">✗</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type DevelopmentBadgeProps = { status: string };

function DevelopmentBadge({ status }: DevelopmentBadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
        status === 'Ready'
          ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
          : status === 'In Pilot'
            ? 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800'
            : 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
      }`}
    >
      {status}
    </span>
  );
}

export function ProductDetailModal({
  product,
  onClose,
  visibleCountries,
}: ProductDetailModalProps) {
  const deviceImage = getDeviceImage(product.solution);
  const isSkytabDevice = product.platform?.includes('Shift4 Dine') && deviceImage;

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title={product.solution}
      subtitle="Product Details"
      maxWidth="5xl"
    >
      {isSkytabDevice ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-lg p-6 flex items-center justify-center min-h-[300px]">
              <img
                src={deviceImage!}
                alt={product.solution}
                className="max-w-full max-h-[280px] object-contain rounded-lg"
                onError={e => {
                  (e.target as HTMLImageElement).src = '/skytab-overview.png';
                }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">Platform</h3>
              <p className="text-sm text-muted-foreground">{product.platform}</p>
            </div>

            {product.solution_description && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Description</h3>
                <p className="text-sm text-muted-foreground">{product.solution_description}</p>
              </div>
            )}

            {product.vertical && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Vertical</h3>
                <p className="text-sm text-muted-foreground">{product.vertical}</p>
              </div>
            )}

            {product.development && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">Development Status</h3>
                <DevelopmentBadge status={product.development} />
              </div>
            )}

            {product.countries && (
              <CountryAvailability product={product} visibleCountries={visibleCountries} />
            )}

            {product.notes && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">Additional Notes</h3>
                <div className="bg-muted/50 border border-border rounded-md p-3">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{product.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-foreground mb-1">Platform</h3>
            <p className="text-sm text-muted-foreground">{product.platform}</p>
          </div>

          {product.solution_description && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">Description</h3>
              <p className="text-sm text-muted-foreground">{product.solution_description}</p>
            </div>
          )}

          {product.vertical && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">Vertical</h3>
              <p className="text-sm text-muted-foreground">{product.vertical}</p>
            </div>
          )}

          {product.development && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">Development Status</h3>
              <DevelopmentBadge status={product.development} />
            </div>
          )}

          {product.countries && (
            <CountryAvailability product={product} visibleCountries={visibleCountries} />
          )}

          {product.notes && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Additional Notes</h3>
              <div className="bg-muted/50 border border-border rounded-md p-3">
                <p className="text-sm text-foreground whitespace-pre-wrap">{product.notes}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </BaseModal>
  );
}
